/**
 * First-beat (offset) detection and the note-based auto offset (W2-08 timing; ARCHITECTURE.md §3.2
 * `core/audio-dsp/offset.ts`; DESIGN.md §5.2 "First beat"; rpe-format F§3.4 for the sign).
 *
 * The chart offset is the music time (s) at which beat 0 is heard: beat b plays at `offset + chartSec(b)`, where
 * chartSec is the chart's tempo map (Phira's f64 order; the first tempo also times negative beats).
 *
 * `suggestOffset` (beat-grid phase fitting):
 *  1. Reference: the user's current offset, unless it lies in the silent intro (more than half a beat before the
 *     first clear onset): then the first clear onset. The beat grid is searched within half a beat of it, so the
 *     suggestion keeps the user's choice of which beat is beat 0 and only fixes the phase.
 *  2. Phase: the offset whose grid collects the most onset energy (envelope read on every beat of the song, quarter-
 *     hop steps), then a weighted median of the distances between grid beats and their onsets (exact onset times
 *     from the PCM when `refineOnset` is given), twice. With `lowEnvelope` (the 30-200 Hz onsets the audio worker
 *     computes from the PCM) the low band's grid score is added, weighted by how rhythmic it is: kick drums and bass
 *     sit on the beat, so hi-hats or claps between the beats no longer pull the grid half a beat off.
 *  3. Confidence 0..1: how well the grid explains the onsets (F-measure, tempo.ts `gridFit` generalised to tempo
 *     maps) times the contrast of the best phase over the average phase, times the consistency of the per-beat
 *     deviations (median absolute deviation: 1 below 3 ms, 0 at 25 ms), times the clarity of the phase: when the
 *     grid half a beat away scores more than half as well, the confidence falls, to 0 when both score the same
 *     (the user is then told to check by ear instead of getting a confident answer that may be the off-beat).
 *
 * `autoOffset` (P1 "Fine-tune with my notes", DESIGN.md §5.2.6): normalised cross-correlation between the onset
 * envelope and a note signal (one Gaussian of 12 ms per note time) for shifts within +-searchMs; the result is the
 * shift that maximises it (positive = the music is later than the notes: raise the offset by that much) and the
 * correlation itself (bands: < 0.35 poor, 0.6-0.75 typical).
 */

import { envelopeAt } from './onset';
import { beatTolerance, nearestPeak, onsetPeaks, type OnsetPeak } from './tempo';
import type { OnsetEnvelope } from './types';

/** Tempo point as the worker protocol sends it: beat (f64) and BPM. */
export interface BpmPointF64 {
  readonly beat: number;
  readonly bpm: number;
}

/** A tempo map over f64 beats in Phira's operation order (DESIGN.md §6, rpe-format F§3.2). */
export interface F64Tempo {
  /** Chart seconds of a beat (negative beats use the first tempo). */
  secAt(beat: number): number;
  /** Beat at chart seconds. */
  beatAt(sec: number): number;
  /** Seconds per beat at a beat. */
  periodAt(beat: number): number;
  /** True when the tempo changes somewhere (not one constant BPM). */
  readonly hasChanges: boolean;
}

interface Seg {
  readonly beat: number;
  readonly time: number;
  readonly bpm: number;
}

/** Sorted, with a point at beat 0 (the first point's tempo when the list does not start at 0); invalid BPMs dropped. */
export function createF64Tempo(points: readonly BpmPointF64[]): F64Tempo {
  const valid = points
    .filter((p) => Number.isFinite(p.beat) && Number.isFinite(p.bpm) && p.bpm > 0)
    .slice()
    .sort((a, b) => a.beat - b.beat);
  const first = valid[0] ?? { beat: 0, bpm: 120 };
  const list: BpmPointF64[] =
    first.beat > 0 ? [{ beat: 0, bpm: first.bpm }, ...valid] : valid.length > 0 ? valid : [first];
  const segs: Seg[] = [];
  for (const p of list) {
    const prev = segs[segs.length - 1];
    const time = prev === undefined ? 0 : prev.time + (p.beat - prev.beat) * (60 / prev.bpm);
    segs.push({ beat: p.beat, time, bpm: p.bpm });
  }
  const segAtBeat = (b: number): Seg => {
    let s = segs[0] as Seg;
    for (const x of segs) {
      if (x.beat <= b) s = x;
      else break;
    }
    return s;
  };
  const segAtTime = (t: number): Seg => {
    let s = segs[0] as Seg;
    for (const x of segs) {
      if (x.time <= t) s = x;
      else break;
    }
    return s;
  };
  return {
    secAt: (b) => {
      const s = segAtBeat(b);
      return s.time + (b - s.beat) * (60 / s.bpm);
    },
    beatAt: (t) => {
      const s = segAtTime(t);
      return s.beat + (t - s.time) / (60 / s.bpm);
    },
    periodAt: (b) => 60 / segAtBeat(b).bpm,
    hasChanges: segs.some((x) => x.bpm !== segs[0]?.bpm),
  };
}

/** Music times of every whole beat heard inside [0, durationSec] for a given offset. */
export function gridTimes(tempo: F64Tempo, offsetSec: number, durationSec: number): number[] {
  const out: number[] = [];
  const b0 = Math.ceil(tempo.beatAt(-offsetSec) - 1e-9);
  for (let b = b0; ; b++) {
    const t = offsetSec + tempo.secAt(b);
    if (t > durationSec) break;
    if (t >= 0) out.push(t);
    if (out.length > 200000) break;
  }
  return out;
}

export interface OffsetOptions {
  /** Exact onset time near `approxSec` (within `windowSec`), e.g. from the PCM's fine novelty. */
  readonly refineOnset?: (approxSec: number, windowSec: number) => number | null;
  /**
   * Onset envelope of the low band (onset.ts `lowOnsetEnvelope`, same hop): kick drums and bass mark the beat, so it
   * decides between a phase and the one half a beat away when both collect onsets (hi-hats between the beats).
   */
  readonly lowEnvelope?: OnsetEnvelope;
}

export interface OffsetSuggestion {
  /** Suggested chart offset in whole milliseconds. */
  readonly offsetMs: number;
  /** 0..1 */
  readonly confidence: number;
}

/** Mean envelope value on the beat grid of an offset. */
function phaseScore(e: OnsetEnvelope, tempo: F64Tempo, offsetSec: number, durationSec: number): number {
  const times = gridTimes(tempo, offsetSec, durationSec);
  if (times.length === 0) return 0;
  let s = 0;
  for (const t of times) s += envelopeAt(e, t);
  return s / times.length;
}

/** Weighted median of values. */
export function weightedMedian(values: readonly number[], weights: readonly number[]): number {
  const idx = values.map((_, i) => i).sort((a, b) => (values[a] ?? 0) - (values[b] ?? 0));
  let total = 0;
  for (const w of weights) total += Math.max(0, w);
  if (!(total > 0)) return 0;
  let acc = 0;
  for (const i of idx) {
    acc += Math.max(0, weights[i] ?? 0);
    if (acc >= total / 2) return values[i] ?? 0;
  }
  return values[idx[idx.length - 1] ?? 0] ?? 0;
}

/** Deviations (onset - grid beat) of the onsets matched to the grid, with their weights. */
function deviations(
  peaks: readonly OnsetPeak[],
  tempo: F64Tempo,
  offsetSec: number,
  durationSec: number,
  refine: OffsetOptions['refineOnset'],
  tolScale: number,
): { readonly d: number[]; readonly w: number[] } {
  const d: number[] = [];
  const w: number[] = [];
  for (const g of gridTimes(tempo, offsetSec, durationSec)) {
    const tol = beatTolerance(tempo.periodAt(tempo.beatAt(g - offsetSec))) * tolScale;
    const p = nearestPeak(peaks, g, tol);
    if (p === null) continue;
    const exact = refine?.(p.t, Math.min(tol, 0.03)) ?? null;
    d.push((exact ?? p.t) - g);
    w.push(p.v);
  }
  return { d, w };
}

/** Grid fit over a tempo map: precision (beats with an onset) and recall (onset energy on the grid). */
function mapFit(peaks: readonly OnsetPeak[], tempo: F64Tempo, offsetSec: number, durationSec: number): number {
  const first = peaks[0];
  const last = peaks[peaks.length - 1];
  if (first === undefined || last === undefined) return 0;
  let total = 0;
  let hit = 0;
  for (const p of peaks) {
    total += p.v;
    const beat = tempo.beatAt(p.t - offsetSec);
    const g = offsetSec + tempo.secAt(Math.round(beat));
    if (Math.abs(p.t - g) <= beatTolerance(tempo.periodAt(beat))) hit += p.v;
  }
  let beats = 0;
  let matched = 0;
  for (const g of gridTimes(tempo, offsetSec, durationSec)) {
    const tol = beatTolerance(tempo.periodAt(tempo.beatAt(g - offsetSec)));
    if (g < first.t - tol || g > last.t + tol) continue;
    beats++;
    if (nearestPeak(peaks, g, tol) !== null) matched++;
  }
  const recall = total > 0 ? hit / total : 0;
  const precision = beats > 0 ? matched / beats : 0;
  return precision + recall > 0 ? (2 * precision * recall) / (precision + recall) : 0;
}

function quantile(values: readonly number[], q: number): number {
  if (values.length === 0) return 0;
  const s = [...values].sort((a, b) => a - b);
  return s[Math.min(s.length - 1, Math.max(0, Math.floor(q * (s.length - 1))))] ?? 0;
}

/** The first onset clearly above the song's typical onset strength (null for silence). */
export function firstClearOnset(peaks: readonly OnsetPeak[]): OnsetPeak | null {
  const q = quantile(
    peaks.map((p) => p.v),
    0.9,
  );
  return peaks.find((p) => p.v >= 0.3 * q) ?? null;
}

/** Whole-beat shifts of beat 0 considered for tempo maps with changes (seconds either way). */
const MAX_SHIFT_SEC = 8;
/** Grid scores within this ratio of the best are ties (resolved towards the reference). */
const SHIFT_TIE = 0.03;

/** Weight of the low-band phase evidence at full contrast (the full-band evidence weighs 1). */
const LOW_BAND_WEIGHT = 1;
/** Ambiguity (score of the phase half a beat away / best score) up to which the confidence is not lowered. */
const AMBIGUITY_FREE = 0.5;

/** Phase evidence of an offset: the full-band grid score, plus the low band's when the caller has it. */
interface PhaseEvidence {
  /** Combined score of an offset (comparable across offsets of one search). */
  score(offsetSec: number): number;
}

function phaseEvidence(
  e: OnsetEnvelope,
  low: OnsetEnvelope | undefined,
  tempo: F64Tempo,
  durationSec: number,
  offsets: readonly number[],
): { readonly evidence: PhaseEvidence; readonly values: Float64Array } | null {
  const full = offsets.map((o) => phaseScore(e, tempo, o, durationSec));
  const topFull = maxOf(full);
  if (!(topFull > 0)) return null;
  let lowWeight = 0;
  let topLow = 0;
  let lowScores: number[] | null = null;
  if (low !== undefined && low.env.length > 0) {
    lowScores = offsets.map((o) => phaseScore(low, tempo, o, durationSec));
    topLow = maxOf(lowScores);
    if (topLow > 0) {
      let mean = 0;
      for (const v of lowScores) mean += v;
      mean /= lowScores.length;
      // A low band without rhythm (flat over the phases) has little say.
      lowWeight = LOW_BAND_WEIGHT * Math.max(0, (topLow - mean) / topLow);
    }
  }
  const evidence: PhaseEvidence = {
    score(o) {
      const f = phaseScore(e, tempo, o, durationSec) / topFull;
      if (lowWeight === 0 || low === undefined) return f;
      return f + (lowWeight * phaseScore(low, tempo, o, durationSec)) / topLow;
    },
  };
  const values = new Float64Array(offsets.length);
  for (let i = 0; i < offsets.length; i++) {
    const f = (full[i] ?? 0) / topFull;
    values[i] = lowWeight === 0 || lowScores === null ? f : f + (lowWeight * (lowScores[i] ?? 0)) / topLow;
  }
  return { evidence, values };
}

/**
 * Ambiguity of a phase: the best score of the offsets between a quarter and three quarters of a beat away from
 * `best`, relative to the score of `best` (0 = clear, 1 = the off-beat explains the onsets as well).
 */
function phaseAmbiguity(
  evidence: PhaseEvidence,
  best: number,
  bestScore: number,
  period: number,
  step: number,
): number {
  if (!(bestScore > 0)) return 1;
  let alt = 0;
  for (let d = period / 4; d <= (period * 3) / 4 + 1e-9; d += step) {
    alt = Math.max(alt, evidence.score(best + d), evidence.score(best - d));
  }
  return Math.min(1, alt / bestScore);
}

/** Suggested offset (see the module header). Confidence 0 and the current offset for silence. */
export function suggestOffset(
  e: OnsetEnvelope,
  bpm: readonly BpmPointF64[],
  currentOffsetMs: number,
  opts: OffsetOptions = {},
): OffsetSuggestion {
  const current = Number.isFinite(currentOffsetMs) ? currentOffsetMs : 0;
  const unchanged: OffsetSuggestion = { offsetMs: Math.round(current), confidence: 0 };
  const durationSec = e.env.length * e.hopSec;
  const peaks = onsetPeaks(e);
  const first = firstClearOnset(peaks);
  if (first === null || peaks.length < 2) return unchanged;
  const tempo = createF64Tempo(bpm);
  const period0 = tempo.periodAt(0);
  const currentSec = current / 1000;
  const ref = currentSec < first.t - period0 / 2 ? first.t : currentSec;
  // Phase search: within half a beat of the reference under a constant tempo (every whole-beat shift is
  // equivalent there); over +-MAX_SHIFT_SEC for a tempo map with changes, where only one shift lines up every
  // section (near-ties keep the one nearest the reference).
  const step = e.hopSec / 4;
  const radius = tempo.hasChanges ? MAX_SHIFT_SEC : period0 / 2;
  const offsets: number[] = [];
  for (let o = ref - radius; o < ref + radius; o += step) offsets.push(o);
  const ev = phaseEvidence(e, opts.lowEnvelope, tempo, durationSec, offsets);
  if (ev === null) return unchanged;
  const scores = ev.values;
  let top = 0;
  let sum = 0;
  for (const v of scores) {
    top = Math.max(top, v);
    sum += v;
  }
  if (!(top > 0)) return unchanged;
  const tie = tempo.hasChanges ? SHIFT_TIE : 0;
  let best = ref;
  let bestScore = -Infinity;
  for (let i = 0; i < scores.length; i++) {
    const v = scores[i] ?? 0;
    const isMax = v >= (scores[i - 1] ?? -Infinity) && v >= (scores[i + 1] ?? -Infinity);
    const o = offsets[i] ?? ref;
    if (!isMax || v < top * (1 - tie)) continue;
    if (bestScore === -Infinity || Math.abs(o - ref) < Math.abs(best - ref)) {
      best = o;
      bestScore = v;
    }
  }
  const meanScore = scores.length > 0 ? sum / scores.length : 0;
  const ambiguity = phaseAmbiguity(ev.evidence, best, bestScore, period0, step);
  // Onset deviations: shift by their weighted median (wide window first, then tight).
  let offset = best;
  let spread = Infinity;
  for (const tolScale of [1, 0.5]) {
    const { d, w } = deviations(peaks, tempo, offset, durationSec, opts.refineOnset, tolScale);
    if (d.length === 0) break;
    const shift = weightedMedian(d, w);
    offset += shift;
    spread = weightedMedian(
      d.map((x) => Math.abs(x - shift)),
      w,
    );
  }
  const fit = mapFit(peaks, tempo, offset, durationSec);
  const contrast = bestScore > 0 ? Math.max(0, (bestScore - meanScore) / bestScore) : 0;
  const consistency = Number.isFinite(spread) ? clamp01(1 - (spread * 1000 - 3) / 22) : 0;
  const clarity = clamp01((1 - ambiguity) / (1 - AMBIGUITY_FREE));
  return {
    offsetMs: Math.round(offset * 1000),
    confidence: clamp01(fit * (0.5 + 0.5 * contrast) * consistency * clarity),
  };
}

/** Standard deviation of each note's Gaussian in the note signal (s). */
const NOTE_SIGMA_SEC = 0.012;

export interface AutoOffsetResult {
  /** Shift (ms) that best aligns the notes with the music: positive = raise the offset. */
  readonly correctionMs: number;
  /** Normalised cross-correlation at that shift (-1..1). */
  readonly correlation: number;
}

/** Auto offset against existing note times (music seconds), see the module header. */
export function autoOffset(e: OnsetEnvelope, noteTimesSec: ArrayLike<number>, searchMs: number): AutoOffsetResult {
  const none: AutoOffsetResult = { correctionMs: 0, correlation: 0 };
  const notes: number[] = [];
  for (let i = 0; i < noteTimesSec.length; i++) {
    const t = noteTimesSec[i];
    if (t !== undefined && Number.isFinite(t)) notes.push(t);
  }
  if (notes.length === 0 || e.env.length === 0) return none;
  notes.sort((a, b) => a - b);
  const search = Math.max(0, Math.min(2000, Number.isFinite(searchMs) ? searchMs : 0)) / 1000;
  const step = e.hopSec / 4;
  const pad = search + 4 * NOTE_SIGMA_SEC;
  const from = Math.max(0, (notes[0] ?? 0) - pad);
  const to = (notes[notes.length - 1] ?? 0) + pad;
  const n = Math.max(1, Math.ceil((to - from) / step));
  // Note signal on the fine grid.
  const sig = new Float64Array(n);
  const reach = 4 * NOTE_SIGMA_SEC;
  for (const t of notes) {
    const i0 = Math.max(0, Math.floor((t - reach - from) / step));
    const i1 = Math.min(n - 1, Math.ceil((t + reach - from) / step));
    for (let i = i0; i <= i1; i++) {
      const x = (from + i * step - t) / NOTE_SIGMA_SEC;
      sig[i] = (sig[i] ?? 0) + Math.exp(-0.5 * x * x);
    }
  }
  let sigMean = 0;
  for (let i = 0; i < n; i++) sigMean += sig[i] ?? 0;
  sigMean /= n;
  let sigVar = 0;
  for (let i = 0; i < n; i++) sigVar += ((sig[i] ?? 0) - sigMean) ** 2;
  if (!(sigVar > 0)) return none;
  const lags = Math.round(search / step);
  const values = new Float64Array(2 * lags + 1);
  const x = new Float64Array(n);
  for (let k = -lags; k <= lags; k++) {
    const shift = k * step;
    let mean = 0;
    for (let i = 0; i < n; i++) {
      const v = envelopeAt(e, from + i * step + shift);
      x[i] = v;
      mean += v;
    }
    mean /= n;
    let cov = 0;
    let varX = 0;
    for (let i = 0; i < n; i++) {
      const dx = (x[i] ?? 0) - mean;
      cov += dx * ((sig[i] ?? 0) - sigMean);
      varX += dx * dx;
    }
    values[k + lags] = varX > 0 ? cov / Math.sqrt(varX * sigVar) : 0;
  }
  let bi = 0;
  for (let i = 1; i < values.length; i++) if ((values[i] ?? 0) > (values[bi] ?? 0)) bi = i;
  let delta = 0;
  const a = values[bi - 1];
  const c = values[bi + 1];
  const b = values[bi] ?? 0;
  if (a !== undefined && c !== undefined) {
    const denom = a - 2 * b + c;
    if (denom < 0) delta = Math.max(-0.5, Math.min(0.5, (0.5 * (a - c)) / denom));
  }
  return { correctionMs: Math.round((bi - lags + delta) * step * 1e4) / 10, correlation: b };
}

function maxOf(values: readonly number[]): number {
  let m = 0;
  for (const v of values) if (v > m) m = v;
  return m;
}

function clamp01(x: number): number {
  return Number.isFinite(x) ? Math.max(0, Math.min(1, x)) : 0;
}
